import type { Dimensions, Node, XYPosition } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import {
  CustomNode,
  NodeType,
  createGroupNodeStruct,
} from '@/types/node-types';

type InternalNodeLike = {
  internals: { positionAbsolute: XYPosition };
};

type GroupHelpers = {
  getInternalNode: (id: string) => InternalNodeLike | undefined;
  getNodesBounds: (nodes: Node[]) => { x: number; y: number };
};

/** Canvas-only nodes that can participate in user-created groups. */
export function isGroupEligibleNode(node: CustomNode): boolean {
  return (
    node.type !== NodeType.GroupNode &&
    node.type !== NodeType.ArtifactNode &&
    node.type !== NodeType.SelectNode
  );
}

export function isGroupNode(node: CustomNode): boolean {
  return node.type === NodeType.GroupNode;
}

/** Sorted ids of a group's eligible children — the exact set that keyboard
 * Delete/Backspace and the Ungroup toolbar's actions operate on. */
export function getGroupChildIds(
  nodes: CustomNode[],
  groupId: string,
): string[] {
  return nodes
    .filter((n) => n.parentId === groupId && isGroupEligibleNode(n))
    .map((n) => n.id)
    .sort();
}

/**
 * Marks a group's eligible children as selected and deselects everything
 * else, so keyboard Delete/Backspace and the Ungroup toolbar's Copy/
 * Duplicate/Delete act on the same nodes. Pure transform — callers apply the
 * result via useNodeStore.setState/setNodes.
 */
export function withOnlyGroupChildrenSelected(
  nodes: CustomNode[],
  groupId: string,
): CustomNode[] {
  const childIds = new Set(getGroupChildIds(nodes, groupId));
  return nodes.map((n) => ({ ...n, selected: childIds.has(n.id) }));
}

export const GROUP_NODE_FOCUSED_CLASS = 'group-node--focused';

export type GroupChromeState = 'idle' | 'child-selected' | 'container-focused';

export function getGroupChromeState(
  nodes: CustomNode[],
  groupId: string,
  groupContainerFocusedId: string | null,
): GroupChromeState {
  const groupNode = nodes.find((node) => node.id === groupId);
  if (groupNode?.dragging || groupContainerFocusedId === groupId) {
    return 'container-focused';
  }
  if (nodes.some((node) => node.parentId === groupId && node.selected)) {
    return 'child-selected';
  }
  return 'idle';
}

/** True only when the container is focused (draggable). Idle containers are
 * always visible but not draggable — `syncGroupNodeInteractionState` uses this
 * to set `draggable: true` and the `group-node--focused` class. */
export function isGroupInteractive(
  nodes: CustomNode[],
  groupId: string,
  groupContainerFocusedId: string | null,
): boolean {
  return (
    getGroupChromeState(nodes, groupId, groupContainerFocusedId) ===
    'container-focused'
  );
}

/** @deprecated Use isGroupInteractive instead */
export function isGroupFocused(
  nodes: CustomNode[],
  groupId: string,
  groupContainerFocusedId: string | null,
): boolean {
  return isGroupInteractive(nodes, groupId, groupContainerFocusedId);
}

export function getGroupParentId(
  nodes: CustomNode[],
  nodeId: string,
): string | null {
  const node = nodes.find((entry) => entry.id === nodeId);
  if (!node?.parentId) return null;
  const parent = nodes.find((entry) => entry.id === node.parentId);
  return parent && isGroupNode(parent) ? parent.id : null;
}

export function syncGroupNodeInteractionState(
  nodes: CustomNode[],
  groupContainerFocusedId: string | null,
): CustomNode[] {
  let changed = false;
  const nextNodes = nodes.map((node) => {
    if (!isGroupNode(node)) return node;

    const interactive = isGroupInteractive(
      nodes,
      node.id,
      groupContainerFocusedId,
    );
    const nextDraggable = interactive;
    const nextClassName = interactive ? GROUP_NODE_FOCUSED_CLASS : undefined;

    if (
      node.draggable === nextDraggable &&
      (node.className ?? undefined) === nextClassName
    ) {
      return node;
    }

    changed = true;
    return {
      ...node,
      draggable: nextDraggable,
      className: nextClassName,
    };
  });

  return changed ? nextNodes : nodes;
}

export function getGroupEligibleSelection(nodes: CustomNode[]): CustomNode[] {
  return nodes.filter((node) => node.selected && isGroupEligibleNode(node));
}

export function canGroupSelection(nodes: CustomNode[]): boolean {
  return getGroupEligibleSelection(nodes).length >= 2;
}

/** True when every selected eligible node shares the same group parent. */
export function canUngroupSelection(nodes: CustomNode[]): boolean {
  return getSharedGroupParentId(nodes) !== null;
}

export function getSharedGroupParentId(nodes: CustomNode[]): string | null {
  const selected = getGroupEligibleSelection(nodes);
  if (selected.length === 0) return null;

  // Include undefined parentIds in the dedup so a selection mixing a grouped
  // node with an ungrouped node (parentIds: [groupId, undefined]) doesn't
  // collapse to size 1 and get mistaken for a fully-shared group.
  const parentIds = new Set(selected.map((node) => node.parentId));
  if (parentIds.size !== 1) return null;

  const [parentId] = parentIds;
  if (!parentId) return null;
  const parent = nodes.find((node) => node.id === parentId);
  return parent && isGroupNode(parent) ? parentId : null;
}

export function canUngroupFromContext(
  nodes: CustomNode[],
  clickedNodeId?: string | null,
): boolean {
  if (clickedNodeId) {
    const clicked = nodes.find((node) => node.id === clickedNodeId);
    if (clicked && isGroupNode(clicked)) {
      return nodes.some((node) => node.parentId === clicked.id);
    }
    if (clicked?.parentId) {
      const parent = nodes.find((node) => node.id === clicked.parentId);
      return Boolean(parent && isGroupNode(parent));
    }
  }
  return canUngroupSelection(nodes);
}

export function ungroupNodesFromContext(
  nodes: CustomNode[],
  clickedNodeId: string | null | undefined,
): CustomNode[] {
  if (clickedNodeId) {
    const clicked = nodes.find((node) => node.id === clickedNodeId);
    if (clicked && isGroupNode(clicked)) {
      const childIds = nodes
        .filter((node) => node.parentId === clicked.id)
        .map((node) => node.id);
      if (childIds.length === 0) return nodes;
      return detachNodesFromGroups(nodes, childIds, clicked.id);
    }
    if (clicked?.parentId) {
      const parent = nodes.find((node) => node.id === clicked.parentId);
      if (parent && isGroupNode(parent)) {
        const childIds = nodes
          .filter((node) => node.parentId === parent.id)
          .map((node) => node.id);
        return detachNodesFromGroups(nodes, childIds, parent.id);
      }
    }
  }
  return ungroupSelectedNodes(nodes);
}

/**
 * Detaches the given nodes from their group parent, converting their
 * position to absolute canvas coordinates. Resolves the absolute position
 * from the node tree itself (via getAbsolutePosition) rather than React
 * Flow's measured internals, which can be undefined right after a
 * group/ungroup action and would otherwise silently fall back to leaving
 * the node at its stale parent-relative position.
 */
export function detachNodesFromGroups(
  nodes: CustomNode[],
  ids: string[],
  removeParentId?: string,
): CustomNode[] {
  const idsToDetach = new Set(ids);
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const nextNodes: CustomNode[] = [];

  for (const node of nodes) {
    if (removeParentId === node.id) {
      continue;
    }

    if (idsToDetach.has(node.id) && node.parentId) {
      nextNodes.push({
        ...node,
        position: getAbsolutePosition(node, nodeById),
        parentId: undefined,
        selected: node.selected,
      });
      continue;
    }

    nextNodes.push(node);
  }

  return nextNodes;
}

/**
 * Finds the id of a group node present in `nextNodes` but not
 * `previousNodes` — used to focus a newly created container right after
 * grouping. Diffs by id rather than assuming array position, since
 * groupSelectedNodes returns the input unchanged when grouping fails.
 */
export function findNewGroupId(
  previousNodes: CustomNode[],
  nextNodes: CustomNode[],
): string | null {
  const previousGroupIds = new Set(
    previousNodes.filter(isGroupNode).map((node) => node.id),
  );
  return (
    nextNodes.find(
      (node) => isGroupNode(node) && !previousGroupIds.has(node.id),
    )?.id ?? null
  );
}

export function groupSelectedNodes(
  nodes: CustomNode[],
  selectedIds: string[],
  helpers: GroupHelpers,
): CustomNode[] {
  const selectedSet = new Set(selectedIds);
  const eligibleSelected = nodes.filter(
    (node) => selectedSet.has(node.id) && isGroupEligibleNode(node),
  );
  if (eligibleSelected.length < 2) return nodes;

  let workingNodes = nodes;
  const nestedSelected = eligibleSelected.filter((node) => node.parentId);
  if (nestedSelected.length > 0) {
    workingNodes = detachNodesFromGroups(
      workingNodes,
      nestedSelected.map((node) => node.id),
    );
  }

  const freshSelected = workingNodes.filter(
    (node) => selectedSet.has(node.id) && isGroupEligibleNode(node),
  );
  if (freshSelected.length < 2) return nodes;

  // Bail out atomically if React Flow hasn't computed internal node data for
  // any selected node yet — otherwise it's silently left out of the group,
  // producing a container with fewer children than were selected.
  const internalNodesById = new Map(
    freshSelected.map((node) => [node.id, helpers.getInternalNode(node.id)]),
  );
  if ([...internalNodesById.values()].some((node) => !node)) return nodes;

  const groupId = uuidv4();
  const selectedNodesRectangle = helpers.getNodesBounds(freshSelected);
  const groupNodePosition = {
    x: selectedNodesRectangle.x,
    y: selectedNodesRectangle.y,
  };

  const groupNode: CustomNode = {
    id: groupId,
    type: NodeType.GroupNode,
    position: groupNodePosition,
    data: { struct: createGroupNodeStruct(groupId) },
    selectable: false,
    draggable: false,
    selected: false,
    zIndex: 0,
  };

  const nextNodes = workingNodes.map((node) => {
    if (!selectedSet.has(node.id) || !isGroupEligibleNode(node)) {
      return node;
    }

    const internalNode = internalNodesById.get(node.id);
    if (!internalNode) return node;

    const position = internalNode.internals.positionAbsolute;
    const childZIndex = Math.max(
      typeof node.zIndex === 'number' ? node.zIndex : 1,
      1,
    );
    return {
      ...node,
      position: {
        x: position.x - groupNodePosition.x,
        y: position.y - groupNodePosition.y,
      },
      parentId: groupId,
      selected: false,
      zIndex: childZIndex,
    };
  });

  return [groupNode, ...nextNodes];
}

export function ungroupSelectedNodes(nodes: CustomNode[]): CustomNode[] {
  const sharedParentId = getSharedGroupParentId(nodes);
  if (!sharedParentId) return nodes;

  const childIds = nodes
    .filter(
      (node) => node.parentId === sharedParentId && isGroupEligibleNode(node),
    )
    .map((node) => node.id);

  return detachNodesFromGroups(nodes, childIds, sharedParentId);
}

// ---------------------------------------------------------------------------
// Auto-expand group containers to fit children (from React Flow pro example)
// ---------------------------------------------------------------------------

type Expansion = {
  dimension: Dimensions;
  offset: XYPosition;
  position: XYPosition;
};

type Bounds = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  numberOfChildren: number;
};

function getDimensions(node: Node): Dimensions {
  return {
    width: node.width ?? node.measured?.width ?? 0,
    height: node.height ?? node.measured?.height ?? 0,
  };
}

function addRectToBounds(
  bounds: Bounds,
  position: XYPosition,
  dimensions: Dimensions,
  padding: number,
) {
  const { x, y } = position;
  const x1 = x - padding;
  const y1 = y - padding;
  bounds.x1 = x1 < bounds.x1 ? x1 : bounds.x1;
  bounds.y1 = y1 < bounds.y1 ? y1 : bounds.y1;

  const x2 = x + dimensions.width + padding;
  const y2 = y + dimensions.height + padding;
  bounds.x2 = x2 > bounds.x2 ? x2 : bounds.x2;
  bounds.y2 = y2 > bounds.y2 ? y2 : bounds.y2;
  bounds.numberOfChildren += 1;
}

function getBounds(boundsByParent: Map<string, Bounds>, parentId: string) {
  let bounds = boundsByParent.get(parentId);

  if (!bounds) {
    bounds = {
      x1: Infinity,
      y1: Infinity,
      x2: -Infinity,
      y2: -Infinity,
      numberOfChildren: 0,
    };
    boundsByParent.set(parentId, bounds);
  }

  return bounds;
}

function resolveExpansion(
  parent: Node,
  childBounds: Bounds,
): Expansion | undefined {
  const { x1, y1, x2, y2 } = childBounds;
  const width = x2 - x1;
  const height = y2 - y1;
  const parentDimensions = getDimensions(parent);

  if (
    width === parentDimensions.width &&
    height === parentDimensions.height &&
    x1 === 0 &&
    y1 === 0
  ) {
    return undefined;
  }

  const { x, y } = parent.position;
  const dimension = { width, height };
  const offset = { x: x1, y: y1 };
  const position = { x: x + x1, y: y + y1 };

  return { dimension, offset, position };
}

export function expandGroupNodes<T extends CustomNode>(
  nodes: T[],
  padding = 25,
): T[] {
  const markedForDeletion = new Map<string, Node>();
  const boundsByParent = new Map<string, Bounds>();
  const parentExpansions = new Map<string, Expansion>();

  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    const childBounds = boundsByParent.get(node.id);

    if (
      (!childBounds || childBounds.numberOfChildren === 1) &&
      node.type === NodeType.GroupNode
    ) {
      markedForDeletion.set(node.id, node);
      continue;
    }

    const expansion = childBounds
      ? resolveExpansion(node, childBounds)
      : undefined;

    if (expansion) {
      parentExpansions.set(node.id, expansion);
    }

    if (!node.parentId) continue;

    const bounds = getBounds(boundsByParent, node.parentId);
    addRectToBounds(
      bounds,
      expansion ? expansion.position : node.position,
      expansion ? expansion.dimension : getDimensions(node),
      padding,
    );
  }

  if (parentExpansions.size === 0 && markedForDeletion.size === 0) {
    return nodes;
  }

  const newNodes: T[] = [];
  for (const node of nodes) {
    if (markedForDeletion.has(node.id)) {
      continue;
    }

    const parentMarkedForDeletion = markedForDeletion.get(node.parentId || '');
    if (parentMarkedForDeletion) {
      newNodes.push({
        ...node,
        position: {
          x: node.position.x + parentMarkedForDeletion.position.x,
          y: node.position.y + parentMarkedForDeletion.position.y,
        },
        parentId: undefined,
      } as T);
      continue;
    }

    const expansion = parentExpansions.get(node.id);
    const parentExpansion = parentExpansions.get(node.parentId || '');

    if (expansion && parentExpansion) {
      newNodes.push({
        ...node,
        ...expansion.dimension,
        position: {
          x: expansion.position.x - parentExpansion.offset.x,
          y: expansion.position.y - parentExpansion.offset.y,
        },
      } as T);
      continue;
    }

    if (expansion) {
      newNodes.push({
        ...node,
        ...expansion.dimension,
        position: expansion.position,
      } as T);
      continue;
    }

    if (parentExpansion) {
      newNodes.push({
        ...node,
        position: {
          x: node.position.x - parentExpansion.offset.x,
          y: node.position.y - parentExpansion.offset.y,
        },
      } as T);
      continue;
    }

    newNodes.push(node);
  }

  return newNodes;
}

/**
 * Resolves a node's absolute canvas position by walking up its `parentId`
 * chain — React Flow always stores `position` relative to the parent, so
 * any spatial computation that mixes grouped and ungrouped nodes (overlap
 * detection, bounds, etc.) needs this rather than the raw `position`.
 */
export function getAbsolutePosition(
  node: CustomNode,
  nodeById: Map<string, CustomNode>,
): XYPosition {
  let x = node.position.x;
  let y = node.position.y;
  let current = node.parentId ? nodeById.get(node.parentId) : undefined;
  while (current) {
    x += current.position.x;
    y += current.position.y;
    current = current.parentId ? nodeById.get(current.parentId) : undefined;
  }
  return { x, y };
}

/** Promote children to absolute positions when their group parent is deleted. */
export function promoteGroupChildrenOnDelete(
  nodes: CustomNode[],
  deletingIds: Set<string>,
): CustomNode[] {
  const deletingGroupIds = new Set(
    nodes
      .filter((node) => deletingIds.has(node.id) && isGroupNode(node))
      .map((node) => node.id),
  );
  if (deletingGroupIds.size === 0) return nodes;

  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return nodes
    .filter((node) => !deletingGroupIds.has(node.id))
    .map((node) => {
      if (!node.parentId || !deletingGroupIds.has(node.parentId)) {
        return node;
      }
      return {
        ...node,
        position: getAbsolutePosition(node, nodeById),
        parentId: undefined,
      };
    });
}
